import { Box, HBox, VBox } from "@ui/box/Box"
import { Skeleton } from "@ui/skeleton/Skeleton"
import { Typo } from "@ui/text/Typo"
import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { getCardDisplayStatus, getCardStatusDisplayLabel, preservePermanentCardStatus } from "src/utils/cardStatus"
import { SignalHandler } from "badmfck-signal"
import { CARD_ISSUANCE_FEE, DEFAULT_CURRENCY } from "src/constants"
import { GD } from "src/GD"
import { CardIssuanceForm } from "src/view/components/CardIssuanceForm"
import { CardDetails } from "../../components/CardDetails"
import { S_UNIVERSAL_DIALOG } from "../../components/universalDialog/signals"
import { PRIVATE_MOBILE_BREAKPOINT as MOBILE, PRIVATE_NARROW_BREAKPOINT as NARROW, PRIVATE_THEME as T } from "../theme"
import { getFriendlyErrorMessage } from "src/utils/errors"
import { formatPrivateMoney } from "../money"
import { getPrivateCardNetworkCounts } from "../cardNetworks"
import { EmptyState, PageIntro, PrivateRequestError, PrivateSurface } from "../ui"
import { ActionButton, DS, StatGroup } from "../../ds/Kit"
import { useIssuer } from "../../issuer/IssuerProvider"

const STATUS_COLORS: Record<string, string> = {
	active: T.success,
	frozen: "#D97706",
	blocking: "#D97706",
	pending: "#2563EB",
	created: "#2563EB",
	blocked: T.danger,
	closed: T.danger,
	declined: T.danger,
	error: T.danger,
	inactive: T.muted
}
const MASTERCARD_COLOR = "#D68B39"
const OTHER_NETWORK_COLOR = "#4E7AC7"
const PERCENT = 100
const LAST_FOUR_DIGITS = 4
const EXPIRY_YEAR_DIGITS = 2

export const PrivateCards = ({ onTransactions }: { onTransactions: (cardUid: string) => void }) => {
	const { selectedIssuer, selectedIssuerId } = useIssuer()
	const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY
	const [cards, setCards] = useState<ICardSaved[]>([])
	const [spentByCard, setSpentByCard] = useState<Record<string, number>>({})
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	const load = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const res = await GD.REQ_CARDS.request({ force: true })
			if (res.error) {
				setError(getFriendlyErrorMessage(res.error, "Could not load cards"))
				return
			}
			const responseCards = res.data ?? []
			setCards(previous => responseCards.map(card =>
				preservePermanentCardStatus(previous.find(known => known.uid === card.uid), card)))
			const totals = await Promise.all(responseCards.map(async card => {
				try {
					const transactions = await GD.REQ_COMPANY_TRANSACTIONS_CARD.request({
						card_uid: card.uid,
						page: 0,
						limit: 1,
						tx_type: "spent",
						currency: card.currency || issuerCurrency
					})
					return [card.uid, Number(transactions.data?.totals?.[card.currency || issuerCurrency]?.spent ?? 0)] as const
				} catch {
					return [card.uid, 0] as const
				}
			}))
			setSpentByCard(Object.fromEntries(totals))
		} catch (cause) {
			setError(getFriendlyErrorMessage(cause, "Could not load cards"))
		} finally {
			setLoading(false)
		}
	}, [issuerCurrency])

	useEffect(() => {
		const signals = new SignalHandler()
		signals.add(GD.S_CARD_UPDATED, freshCard => {
			setCards(previous => previous.map(card => card.uid === freshCard.uid
				? preservePermanentCardStatus(card, { ...card, ...freshCard }) : card))
		})
		signals.add(GD.S_CARDS_LIST_UPDATED, incoming => {
			setCards(previous => incoming.map(card =>
				preservePermanentCardStatus(previous.find(known => known.uid === card.uid), card)))
		})
		return () => signals.clear()
	}, [])

	useEffect(() => {
		load()
		return () => S_UNIVERSAL_DIALOG.invoke(null)
	}, [load])

	const openCard = (card: ICardSaved) => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Card Details",
			size: "large",
			content: (
					<CardDetails
						card={card}
						onTransactions={onTransactions}
						onClose={() => {
							S_UNIVERSAL_DIALOG.invoke(null)
							load()
						}}
					/>
			),
			closeDialog: () => {
				S_UNIVERSAL_DIALOG.invoke(null)
				load()
			}
		})
	}

	const createCard = useCallback(() => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Create Card",
			content: (
				<Box data-dialog-layout='compact-form' sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
					<CardIssuanceForm
						context={{ issuer: selectedIssuerId ?? undefined }}
						primaryTitle='Create card'
						defaultIssuanceFee={CARD_ISSUANCE_FEE}
						confirmMessage={feeDisplay =>
							`This will issue a real card and charge a ${feeDisplay} issuance fee to your balance.`
						}
						onCreated={feeDisplay => {
							toast.success(
								`Card request submitted. Activation is in progress. ${feeDisplay} issuance fee applied.`
							)
							S_UNIVERSAL_DIALOG.invoke(null)
							load()
						}}
					/>
				</Box>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}, [load, selectedIssuerId])

	const totalBalance = cards.reduce((sum, card) => sum + Number(card.balance || 0), 0)
	const activeCards = cards.filter(card => card.status === "active").length
	const dailyCapacity = cards.reduce((sum, card) => sum + Number(card.limit_daily || 0), 0)
	const networkCounts = getPrivateCardNetworkCounts(cards)
	const networkMix = [
		{ key: "visa", label: `Visa ${networkCounts.visa}`, count: networkCounts.visa, color: T.accent },
		{ key: "mastercard", label: `Mastercard ${networkCounts.mastercard}`, count: networkCounts.mastercard, color: MASTERCARD_COLOR },
		...(networkCounts.other > 0 ? [{ key: "other", label: `Other ${networkCounts.other}`, count: networkCounts.other, color: OTHER_NETWORK_COLOR }] : [])
	]

	return (
		<VBox gap="16px" sx={{ [MOBILE]: { paddingBottom: "20px" } }}>
			<PageIntro
				title="Cards"
				description={loading
					? <Skeleton mode="manual" busy shape="line" width="230px" height="13px" />
					: error ? "View and manage your virtual cards." : `View and manage ${cards.length} virtual card${cards.length === 1 ? "" : "s"}.`}
				action={selectedIssuerId === null || loading || cards.length === 0 ? undefined : <ActionButton tone="primary" onClick={createCard}><IconPlus /> Create card</ActionButton>}
			/>
			{!loading && cards.length > 0 && (
				<StatGroup
					items={[
						{ label: "Total on cards", value: formatPrivateMoney(totalBalance, issuerCurrency) },
						{ label: "Cards ready", value: `${activeCards} / ${cards.length}` },
						{ label: "Daily capacity", value: formatPrivateMoney(dailyCapacity, issuerCurrency) },
						{
							label: "Card networks",
							value: `${cards.length} ${cards.length === 1 ? "card" : "cards"}`,
							hint: (
								<VBox gap="6px">
									<HBox role="img" aria-label={`Card network mix: ${networkMix.map(item => item.label).join(", ")}`} sx={{ height: "6px", overflow: "hidden", borderRadius: "999px", backgroundColor: DS.line }}>{networkMix.filter(item => item.count > 0).map(item => <Box key={item.key} sx={{ height: "100%", width: `${(item.count / cards.length) * PERCENT}%`, backgroundColor: item.color }} />)}</HBox>
									<HBox gap="12px" sx={{ alignItems: "center", flexWrap: "wrap" }}>{networkMix.map(item => <NetworkKey key={item.key} color={item.color} label={item.label} />)}</HBox>
								</VBox>
							)
						}
					]}
				/>
			)}

			{loading ? (
				<CardsLoadingSkeleton />
			) : error ? (
				<PrivateRequestError message={error} onRetry={load} />
			) : cards.length === 0 ? (
				<PrivateSurface><EmptyState icon={<IconCard />} title="No cards" description={selectedIssuerId === null ? "No issuer account is assigned yet." : "Create a virtual card to start making payments."} action={selectedIssuerId === null ? undefined : <ActionButton tone="primary" onClick={createCard}>Create card</ActionButton>} /></PrivateSurface>
			) : (
				<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "16px", [MOBILE]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
					{cards.map(card => (
						<CardTile
							key={card.uid}
							card={card}
							totalSpent={spentByCard[card.uid] ?? 0}
							onClick={() => openCard(card)}
						/>
					))}
				</Box>
			)}
		</VBox>
	)
}

/**
 * Mirrors the loaded layout (stat group + card tiles) so the page keeps its
 * shape while data arrives instead of swapping through intermediate states.
 */
const CardsLoadingSkeleton = () => (
	<VBox gap="16px">
		<Skeleton mode="manual" busy shape="rect" width="100%" height="88px" sx={{ borderRadius: DS.radius }} />
		<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "16px", [MOBILE]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
			{[0, 1, 2].map(index => (
				<PrivateSurface key={index} sx={{ padding: "12px" }}>
					<VBox gap="12px">
						<Skeleton mode="manual" busy shape="rect" width="100%" height="190px" sx={{ borderRadius: "16px" }} />
						<Skeleton mode="manual" busy shape="rect" width="100%" height="96px" sx={{ borderRadius: "12px" }} />
					</VBox>
				</PrivateSurface>
			))}
		</Box>
	</VBox>
)

const CardTile = ({ card, totalSpent, onClick }: { card: ICardSaved; totalSpent: number; onClick: () => void }) => {
	// Wasabi network codes: 1 = Visa, 3 = Mastercard.
	const isVisa = card.network === 1
	const displayStatus = getCardDisplayStatus(card) ?? card.status
	const statusColor = STATUS_COLORS[displayStatus] ?? T.muted
	const frozen = card.status === "frozen"
	const currency = card.currency || DEFAULT_CURRENCY

	return (
		<PrivateSurface sx={{ padding: "12px" }}>
			<VBox gap="12px">
				<Box
					role="button"
					tabIndex={0}
					aria-label={`Open ${card.name || "card"} details`}
					onClick={onClick}
					onKeyDown={event => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault()
							onClick()
						}
					}}
					sx={{
						width: "100%",
						boxSizing: "border-box",
						cursor: "pointer",
						position: "relative",
						overflow: "hidden",
						borderRadius: "16px",
						padding: "20px",
						minHeight: "178px",
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						color: "#fff",
						background: frozen
							? "linear-gradient(135deg, #475569 0%, #7D879B 100%)"
							: DS.heroGradient,
						boxShadow: "inset 0 0 0 1px var(--ds-card-edge, transparent), 0 12px 28px rgba(15, 23, 42, 0.2)",
						textAlign: "left",
						fontFamily: "inherit",
						transition: "transform .2s ease, box-shadow .2s ease",
						"&::before": { content: "''", position: "absolute", width: "200px", height: "200px", right: "-90px", top: "-100px", borderRadius: "50%", border: "1px solid var(--ds-card-ring, rgba(255,255,255,.2))", boxShadow: "0 0 0 30px rgba(255,255,255,.04), 0 0 0 62px rgba(255,255,255,.025)" },
						[NARROW]: { minHeight: "168px", padding: "18px" },
						"&:focus-visible": { outline: `3px solid ${T.accent}`, outlineOffset: "3px" },
						"&:hover": { transform: "translateY(-2px)", boxShadow: "inset 0 0 0 1px var(--ds-card-edge, transparent), 0 16px 34px rgba(15, 23, 42, 0.28)" }
					}}>
					<HBox sx={{ position: "relative", zIndex: 1, justifyContent: "space-between", alignItems: "flex-start" }}>
						<Typo sx={{ color: "#fff", fontSize: "15px", fontWeight: 700, letterSpacing: "-0.02em" }}>Cardpartner</Typo>
						<Typo sx={{ color: "rgba(255,255,255,.78)", fontSize: "12px", fontWeight: 600 }}>Virtual</Typo>
					</HBox>

					<VBox gap="2px" sx={{ position: "relative", zIndex: 1 }}>
						<Typo sx={{ color: "rgba(255,255,255,0.7)", fontSize: "12px" }}>Balance</Typo>
						<Typo sx={{ color: "#fff", fontWeight: 700, fontSize: "1.7rem", letterSpacing: "-0.04em", fontVariantNumeric: "tabular-nums" }}>{formatPrivateMoney(Number(card.balance || 0), currency)}</Typo>
					</VBox>

					<HBox sx={{ position: "relative", zIndex: 1, justifyContent: "space-between", alignItems: "flex-end" }}>
						<Typo sx={{ color: "rgba(255,255,255,0.85)", fontSize: "13px" }}>{card.name || (card.mask ? `•••• ${card.mask.slice(-LAST_FOUR_DIGITS)}` : "Private card")}</Typo>
						<Typo sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem", fontStyle: "italic" }}>{isVisa ? "VISA" : "mastercard"}</Typo>
					</HBox>
				</Box>
				<HBox sx={{ justifyContent: "space-between", alignItems: "center", padding: "0 4px" }}>
					<HBox gap="8px" sx={{ alignItems: "center" }}><Box aria-hidden sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: statusColor }} /><Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 600 }}>{getCardStatusDisplayLabel(displayStatus)}</Typo></HBox>
					<Typo sx={{ color: DS.muted, fontSize: "13px" }}>{card.providerData?.expiryMonth && card.providerData?.expiryYear ? `Expires ${card.providerData.expiryMonth}/${card.providerData.expiryYear.slice(-EXPIRY_YEAR_DIGITS)}` : "Virtual card"}</Typo>
				</HBox>
				<VBox sx={{ padding: "0 4px" }}>
					<CardInfoRow label="Total spent" value={formatPrivateMoney(totalSpent, currency)} onClick={onClick} />
					<CardInfoRow label="Card name" value={card.name || "Private card"} onClick={onClick} />
					<CardInfoRow label="Card type" value="Virtual" onClick={onClick} />
					<CardInfoRow label="Daily limit" value={formatPrivateMoney(Number(card.limit_daily || 0), currency)} onClick={onClick} />
					<CardInfoRow label="Monthly limit" value={formatPrivateMoney(Number(card.limit_monthly || 0), currency)} onClick={onClick} />
				</VBox>
				<ActionButton tone="secondary" onClick={onClick}>View details <span aria-hidden="true">↗</span></ActionButton>
			</VBox>
		</PrivateSurface>
	)
}

const CardInfoRow = ({ label, value, onClick }: { label: string; value: string; onClick: () => void }) => (
	<Box element="button" type="button" onClick={onClick} sx={{ width: "100%", padding: "10px 4px", border: 0, borderTop: `1px solid ${DS.line}`, background: "transparent", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", fontFamily: "inherit", cursor: "pointer", textAlign: "left", "&:hover": { backgroundColor: DS.soft }, "&:focus-visible": { outline: `2px solid ${T.accent}`, outlineOffset: "-2px" } }}>
		<Typo sx={{ color: DS.muted, fontSize: "13px" }}>{label}</Typo>
		<HBox gap="8px" sx={{ alignItems: "center", minWidth: 0 }}><Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{value}</Typo><Typo sx={{ color: DS.faint, fontSize: "1.1rem" }}>›</Typo></HBox>
	</Box>
)

const IconPlus = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
const IconCard = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3.5" y="6" width="17" height="12" rx="2.2" stroke="currentColor" strokeWidth="1.8" /><path d="M3.5 10h17M7 14.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>

const NetworkKey = ({ color, label }: { color: string; label: string }) => <HBox gap="6px" sx={{ alignItems: "center" }}><Box aria-hidden sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: color }} /><Typo sx={{ color: DS.muted, fontSize: "12px" }}>{label}</Typo></HBox>
